import React, { useEffect, useState } from 'react';
import { adminApi } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Video as VideoIcon, RefreshCw, Shuffle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import AdminVideoFormRow, { EMPTY_VIDEO } from './AdminVideoFormRow';

export default function AdminVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [refreshingSeed, setRefreshingSeed] = useState(false);
  const [freeCount, setFreeCount] = useState(3);
  const [applyingPolicy, setApplyingPolicy] = useState(false);

  const refreshSeed = async () => {
    if (!window.confirm(
      'This will refresh the titles & descriptions of the original seeded videos to the latest Coach Lewis branded copy. It will NOT touch any videos you have added manually. Continue?'
    )) return;
    setRefreshingSeed(true);
    try {
      const res = await adminApi.refreshSeedVideos();
      const parts = [`updated ${res.updated}`];
      if (res.inserted) parts.push(`added ${res.inserted}`);
      if (res.preserved) parts.push(`preserved ${res.preserved} (admin-edited)`);
      if (res.skipped) parts.push(`unchanged ${res.skipped}`);
      toast.success(parts.join(' · '));
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not refresh seed videos.');
    } finally {
      setRefreshingSeed(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const { videos: v } = await adminApi.listVideos();
      setVideos(v || []);
    } catch (e) {
      logger.error('admin videos load failed', e);
      toast.error(e.message || 'Could not load videos.');
    } finally {
      setLoading(false);
    }
  };

  const applyPreviewPolicy = async () => {
    const n = Math.max(0, Math.min(videos.length, Number(freeCount) || 0));
    if (!window.confirm(
      `Randomly pick ${n} video${n === 1 ? '' : 's'} to remain full-access for free users. ` +
      `The other ${videos.length - n} will play as 30-second previews. ` +
      `Re-run anytime to rotate which videos are free. Continue?`
    )) return;
    setApplyingPolicy(true);
    try {
      const res = await adminApi.applyVideoPreviewPolicy(n, 'pro');
      toast.success(
        `${res.free_count} free \u00b7 ${res.locked_count} preview` +
        (res.free_titles?.length ? ` \u2014 free: ${res.free_titles.slice(0, 3).join(', ')}${res.free_titles.length > 3 ? '\u2026' : ''}` : '')
      );
      await refresh();
    } catch (e) {
      toast.error(e.message || 'Could not apply preview policy.');
    } finally {
      setApplyingPolicy(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleCreate = async (form) => {
    setBusy(true);
    try {
      const v = await adminApi.createVideo(form);
      setVideos((prev) => [v, ...prev]);
      toast.success('Video added.');
      setCreating(false);
    } catch (e) { toast.error(e.message || 'Could not add video.'); }
    finally { setBusy(false); }
  };

  const handleUpdate = async (form) => {
    setBusy(true);
    try {
      const v = await adminApi.updateVideo(editingId, form);
      setVideos((prev) => prev.map((x) => (x.video_id === editingId ? v : x)));
      toast.success('Saved.');
      setEditingId(null);
    } catch (e) { toast.error(e.message || 'Could not save.'); }
    finally { setBusy(false); }
  };

  const handleDelete = async (video_id) => {
    if (!window.confirm('Delete this video? This cannot be undone.')) return;
    try {
      await adminApi.deleteVideo(video_id);
      setVideos((prev) => prev.filter((x) => x.video_id !== video_id));
      toast.success('Deleted.');
    } catch (e) { toast.error(e.message || 'Could not delete.'); }
  };

  return (
    <div className="space-y-4" data-testid="admin-videos-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <VideoIcon className="h-4 w-4 text-[#004C54]" />
          <h2 className="font-heading text-lg font-bold">Videos</h2>
          <Badge variant="outline" className="border-foreground/15 text-foreground/60">{videos.length}</Badge>
        </div>
        {!creating && (
          <div className="flex items-center gap-2">
            <Button
              onClick={refreshSeed}
              disabled={refreshingSeed}
              variant="outline"
              data-testid="admin-video-refresh-seed"
              title="Apply the latest Coach Lewis branded titles & descriptions to the original seed videos"
            >
              <RefreshCw className={`h-4 w-4 mr-1 ${refreshingSeed ? 'animate-spin' : ''}`} />
              Refresh seed copy
            </Button>
            <Button onClick={() => setCreating(true)} className="bg-[#004C54] hover:bg-[#003a40] text-white" data-testid="admin-video-add">
              <Plus className="h-4 w-4 mr-1" /> Add video
            </Button>
          </div>
        )}
      </div>

      {creating && (
        <AdminVideoFormRow
          initial={EMPTY_VIDEO}
          isCreate
          busy={busy}
          onCancel={() => setCreating(false)}
          onSubmit={handleCreate}
        />
      )}

      {/* Preview gating: how many videos are full-access for free users */}
      {!creating && videos.length > 0 && (
        <Card className="border-border bg-[#F0F8F9]/40" data-testid="admin-videos-preview-policy">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#004C54]/10 shrink-0">
                <Lock className="h-4 w-4 text-[#004C54]" />
              </div>
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold text-[#003a40] leading-tight">
                  Preview gating
                </div>
                <div className="text-[12px] text-foreground/60 leading-tight mt-0.5">
                  {(() => {
                    const free = videos.filter((v) => (v.tier_min || 'free') === 'free').length;
                    return `${free} of ${videos.length} videos play full-length for free users \u00b7 ${videos.length - free} show 30-second previews.`;
                  })()}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <label htmlFor="preview-free-count" className="text-[12px] uppercase tracking-wider text-foreground/55 font-semibold">
                Free
              </label>
              <Input
                id="preview-free-count"
                type="number"
                min={0}
                max={videos.length}
                value={freeCount}
                onChange={(e) => setFreeCount(e.target.value)}
                className="h-9 w-16 text-center"
                data-testid="admin-videos-free-count-input"
              />
              <Button
                onClick={applyPreviewPolicy}
                disabled={applyingPolicy}
                className="bg-[#004C54] hover:bg-[#003a40] text-white"
                data-testid="admin-videos-apply-preview-policy"
              >
                <Shuffle className={`h-4 w-4 mr-1.5 ${applyingPolicy ? 'animate-spin' : ''}`} />
                {applyingPolicy ? 'Applying\u2026' : 'Apply (random)'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      )}

      {!loading && videos.length === 0 && !creating && (
        <Card className="border-border">
          <CardContent className="p-6 text-center text-foreground/60">
            No videos yet. Click “Add video” to seed your library.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2" data-testid="admin-videos-list">
        {videos.map((v) => (
          editingId === v.video_id ? (
            <AdminVideoFormRow
              key={v.video_id}
              initial={{
                title: v.title || '',
                description: v.description || '',
                youtube_id: v.youtube_id || '',
                category: v.category || '',
                duration_seconds: v.duration_seconds || 0,
                tier_min: v.tier_min || 'free',
                sort_order: v.sort_order ?? 100,
              }}
              busy={busy}
              onCancel={() => setEditingId(null)}
              onSubmit={handleUpdate}
            />
          ) : (
            <Card key={v.video_id} className="border-border" data-testid={`admin-video-row-${v.video_id}`}>
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-[100px_1fr_auto] gap-3 items-center">
                <img
                  src={`https://img.youtube.com/vi/${v.youtube_id}/default.jpg`}
                  alt=""
                  className="w-[100px] h-[60px] object-cover rounded-md bg-[#0a0d0e]"
                />
                <div className="min-w-0">
                  <div className="font-semibold text-[14px] truncate">{v.title}</div>
                  <div className="text-[12px] text-foreground/60 line-clamp-1">{v.description}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <Badge variant="outline" className="border-foreground/15 text-foreground/70 text-[10px]">{v.category}</Badge>
                    <Badge variant="outline" className="border-foreground/15 text-foreground/70 text-[10px] capitalize">{v.tier_min}</Badge>
                    <Badge variant="outline" className="border-foreground/15 text-foreground/70 text-[10px] font-mono">{Math.round((v.duration_seconds||0)/60)}m</Badge>
                  </div>
                </div>
                <div className="flex gap-1.5 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setEditingId(v.video_id)} data-testid={`admin-video-edit-${v.video_id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => handleDelete(v.video_id)} data-testid={`admin-video-delete-${v.video_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        ))}
      </div>
    </div>
  );
}
